import React, { useMemo } from "react";
import { Card, Typography, Empty, Button, Divider } from "antd";
import { useNavigate } from "react-router-dom";

const { Text } = Typography;

export default function EmployeeStatusPanel({ employees = [] }) {
  const navigate = useNavigate();

  const stats = useMemo(() => {
    const activeEmployees = employees.filter(
      (emp) =>
        emp?.employee_status !== "resigned" &&
        emp?.employee_status !== "terminated"
    );

    let fulltime = 0;
    let contract = 0;
    let probation = 0;

    let office = 0;
    let hybrid = 0;
    let remote = 0;

    activeEmployees.forEach((emp) => {
      /*
       * EMPLOYMENT STATUS
       *
       * Probation is treated as a separate status.
       * A probationary employee is NOT counted again
       * under Full-time / Contract.
       */
      if (emp.employeeType === "probationary") {
        probation++;
      } else if (emp.employment_type === "ftime") {
        fulltime++;
      } else if (emp.employment_type === "contract") {
        contract++;
      }

      /*
       * WORK LOCATION
       */
      if (emp.work_location === "office") {
        office++;
      } else if (emp.work_location === "hybrid") {
        hybrid++;
      } else if (emp.work_location === "remote") {
        remote++;
      }
    });

    const total = activeEmployees.length;

    return {
      total,

      fulltime,
      contract,
      probation,

      office,
      hybrid,
      remote,

      employmentKnown: fulltime + contract + probation,
      locationKnown: office + hybrid + remote,
    };
  }, [employees]);

  if (stats.total === 0) {
    return (
      <Card title="Employee Status">
        <Empty
          description={
            <>
              <Text strong>No employees added</Text>
              <br />
              <Text type="secondary">
                Add employees to view workforce distribution and status
              </Text>
            </>
          }
        >
          <Button
            type="primary"
            onClick={() => navigate("/hrpayroll/employees")}
          >
            Add Employee
          </Button>
        </Empty>
      </Card>
    );
  }

  const employmentData = [
    {
      label: "Full-time",
      value: stats.fulltime,
      color: "#f3b704",
    },
    {
      label: "Contract",
      value: stats.contract,
      color: "#0d3b66",
    },
    {
      label: "Probation",
      value: stats.probation,
      color: "#c62828",
    },
  ];

  const locationData = [
    {
      label: "On-Site",
      value: stats.office,
      color: "#2e0fdf",
    },
    {
      label: "Hybrid",
      value: stats.hybrid,
      color: "#247dc7",
    },
    {
      label: "WFH",
      value: stats.remote,
      color: "#18c22f",
    },
  ];

  const renderDistribution = (items) => (
    <>
      <div
        style={{
          display: "flex",
          height: 10,
          borderRadius: 10,
          overflow: "hidden",
          background: "#f1f3f5",
          marginBottom: 14,
        }}
      >
        {items.map((item) => {
          const percentage =
            stats.total > 0 ? (item.value / stats.total) * 100 : 0;

          return (
            <div
              key={item.label}
              style={{
                width: `${percentage}%`,
                background: item.color,
                transition: "width 0.3s ease",
              }}
            />
          );
        })}
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          borderTop: "1px solid #eee",
          borderLeft: "1px solid #eee",
        }}
      >
        {items.map((item) => (
          <div
            key={item.label}
            style={{
              padding: "10px 12px",
              borderRight: "1px solid #eee",
              borderBottom: "1px solid #eee",
            }}
          >
            <Text style={{ fontSize: 13 }}>
              <span
                style={{
                  display: "inline-block",
                  width: 7,
                  height: 7,
                  background: item.color,
                  borderRadius: "50%",
                  marginRight: 6,
                }}
              />

              {item.label}
            </Text>

            <div
              style={{
                fontSize: 20,
                fontWeight: 600,
                marginTop: 5,
              }}
            >
              {item.value}
            </div>
          </div>
        ))}
      </div>
    </>
  );

  return (
    <Card title="Employee Status">
      {/* Total */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 10,
        }}
      >
        <Text type="secondary">Total Employee</Text>

        <span
          style={{
            fontSize: 24,
            fontWeight: 600,
          }}
        >
          {stats.total}
        </span>
      </div>

      {/* Employment Status */}
      <Text
        strong
        style={{
          display: "block",
          fontSize: 12,
          color: "#777",
          marginBottom: 8,
          textTransform: "uppercase",
        }}
      >
        Employment Status
      </Text>

      {renderDistribution(employmentData)}

      <Divider style={{ margin: "18px 0 14px" }} />

      {/* Work Location */}
      <Text
        strong
        style={{
          display: "block",
          fontSize: 12,
          color: "#777",
          marginBottom: 8,
          textTransform: "uppercase",
        }}
      >
        Work Location
      </Text>

      {renderDistribution(locationData)}
    </Card>
  );
}